'use client';

import { useTranslation } from 'next-i18next';
import { Button, Anchor } from '@components/common';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { FloatingNavBar } from './FloatingNavBar';
import { FloatingNavBarOverflowMenu } from './FloatingNavBarOverflowMenu';

export type CompanyNavButton = 'new-job' | 'services' | 'company-profile';

const SELECTED_CLASS = '!border !border-ink !bg-surface !text-ink hover:!border-ink hover:!bg-surface hover:!text-ink focus:!ring-0 !cursor-default';
const DEFAULT_CLASS = '!border !border-input !bg-surface !text-ink hover:!border-ink hover:!bg-surface-50 hover:!text-ink focus:!ring-0';
const BTN_CLASS = '!rounded-2xl !px-5 !py-3 !text-lg !shadow-md hover:!no-underline whitespace-nowrap';

/**
 * @description Company-specific floating nav bar shown on all company MC views.
 *
 * Buttons (left → right):
 * - **Services** — selected on the MC hire landing page; clicking elsewhere returns to /mc.
 * - **New Job** — navigates to the add-job flow (/mc/job-add/edit), auth-guarded.
 - **Company profile** — navigates to /mc/company/edit, auth-guarded.
 * - **Switch to job search** — mirrors the sidebar context switch (works for guests too).
 *
 * Pass `selectedButton` to mark the current page (adds border, disables click).
 * Pass `inline` to render as an in-flow element instead of a fixed overlay.
 */
export const CompanyFloatingNavBar = ({
  locale,
  selectedButton,
  pillBackground,
  inline,
  onNewJobAction,
  isAuthenticated = false,
}: {
  locale: Locale;
  selectedButton?: CompanyNavButton;
  pillBackground?: 'surface' | 'surface-100';
  inline?: boolean;
  /** Called when the New Job button is clicked. Caller handles auth redirect. */
  onNewJobAction?: () => void;
  isAuthenticated?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { setContext } = useMissionControlContext();
  const { router } = useExtendedRouter();
  const newJobClass = `${BTN_CLASS} ${selectedButton === 'new-job' ? SELECTED_CLASS : DEFAULT_CLASS}`;
  const companyProfileClass = `${BTN_CLASS} ${selectedButton === 'company-profile' ? SELECTED_CLASS : DEFAULT_CLASS}`;

  const handleSwitchToJobSearch = () => {
    setContext('candidate');

    // Already on /mc: the context change re-renders MissionControl in place via the
    // dispatched mc-context-change event. router.push would do a full page reload
    // (useExtendedRouter falls back to window.location.assign in App Router),
    // which briefly flashes the previous view. Clean any search/hash via pushState.
    // On dedicated /mc/* pages, navigate so the user actually lands on MC.
    const mcPath = getI18nLinkHref('/mc', locale);
    if (typeof window === 'undefined') return;

    if (window.location.pathname === mcPath) {
      if (window.location.search || window.location.hash) {
        window.history.pushState(null, '', mcPath);
      }
      return;
    }

    void router.push('/mc');
  };

  const renderNewJobButton = () => {
    if (!isAuthenticated) {
      return (
        <Anchor
          href={`/login?_target_path=${encodeURIComponent(`/${locale}/mc/job-add/edit`)}`}
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
        >
          {t('homepage.react.button.addjob')}
        </Anchor>
      );
    }

    if (onNewJobAction) {
      return (
        <Button
          type="button"
          styling="solid"
          color="light"
          className={newJobClass}
          onClick={selectedButton === 'new-job' ? undefined : onNewJobAction}
        >
          {t('homepage.react.button.addjob')}
        </Button>
      );
    }

    return (
      <Anchor
        href={`/${locale}/mc/job-add/edit`}
        styling="solid"
        color="light"
        className={newJobClass}
      >
        {t('homepage.react.button.addjob')}
      </Anchor>
    );
  };

  const renderCompanyProfileButton = () => {
    if (!isAuthenticated) {
      return (
        <Anchor
          href={`/login?_target_path=${encodeURIComponent(`/${locale}/mc/company/edit`)}`}
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
        >
          {t('label.company.profile')}
        </Anchor>
      );
    }

    return (
      <Anchor
        href={`/${locale}/mc/company/edit`}
        styling="solid"
        color="light"
        className={companyProfileClass}
      >
        {t('label.company.profile')}
      </Anchor>
    );
  };

  return (
    <FloatingNavBar pillBackground={pillBackground} inline={inline}>
      <FloatingNavBarOverflowMenu>

        {/* Services — selected on MC hire landing; links to /mc on other pages */}
        {selectedButton === 'services' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('header.label.pricing')}
          </Button>
        ) : (
          <Anchor
            href={`/${locale}/mc`}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('header.label.pricing')}
          </Anchor>
        )}

        {/* New Job */}
        {renderNewJobButton()}

        {/* Company profile */}
        {renderCompanyProfileButton()}

        {/* Context Switch — mirrors the sidebar switch (go to job search). */}
        <Button
          type="button"
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          onClick={handleSwitchToJobSearch}
        >
          {t('mc.switch.for.candidates')}
        </Button>

      </FloatingNavBarOverflowMenu>
    </FloatingNavBar>
  );
};
